import { Policy, RuleModel } from '../../../../types';
import SimpleDrawer from '../../../shared/simple-drawer';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRuleRunLogsCount, RulesKeys } from '../../../api/rules-engine-api';
import RulesEngineRunLogsGrid from './rules-engine-run-logs-grid';
import { StatCard } from '../../../shared/stat-card';
import {
  isAbleToSeeUIElement,
  RulesEngineRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';

import RulesDetailsSummaryOld from './rules-details-summary-old';
import StatusLabel from '../../../shared/status-label';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
interface RulesEngineDrawerDetailsOldProps {
  selectedRule: RuleModel;
  allPolicies: Policy[] | undefined;
  onEdit: (rule: RuleModel) => void;
  onEnable: (rule: RuleModel) => void;
  onDisable: (rule: RuleModel) => void;
  onDelete: (rule: RuleModel) => void;
  onClone: (rule: RuleModel) => void;
  onClose: () => void;
  rules: RuleModel[];
  executionCountsData?: {
    total: number | React.ReactNode;
    successful: number | React.ReactNode;
    failed: number | React.ReactNode;
  };
}

export default function RulesEngineDrawerDetailsOld({
  selectedRule,
  allPolicies,
  onEdit,
  onEnable,
  onDisable,
  onDelete,
  onClone,
  onClose,
  rules,
  executionCountsData,
}: RulesEngineDrawerDetailsOldProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [tabValue, setTabValue] = useState(0);
  const { rulesEngineRoles } = useUserSettings();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isEnabled = selectedRule.basicInfo.status === 'Enabled';

  const { data: countsData, isLoading: isLoadingLogs } = useQuery({
    queryKey: [RulesKeys.RunLogs, 'count', selectedRule.id],
    queryFn: () => getRuleRunLogsCount(selectedRule.id),
    enabled: !!selectedRule?.id && !executionCountsData,
  });

  const { executionCounts, lastTriggered } = useMemo(() => {
    return {
      executionCounts: executionCountsData ||
        countsData || {
          total: 0,
          successful: 0,
          failed: 0,
        },
      lastTriggered: countsData?.lastTriggered || null,
    };
  }, [countsData, executionCountsData]);

  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  const canEditRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.EditRules.value
  );
  const canChangeRuleStatus = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.ChangeRuleStatus.value
  );
  const canCloneRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.AddRules.value
  );
  const canDeleteRules = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.DeleteRules.value
  );

  const editDisabledReason = canEditRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.EditRules.name);
  const changeRuleStatusDisabledReason = canChangeRuleStatus
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.ChangeRuleStatus.name);
  const cloneDisabledReason = canCloneRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.AddRules.name);
  const deleteDisabledReason = canDeleteRules
    ? undefined
    : Messages.Errors.Permission.Acting(RulesEngineRoles.DeleteRules.name);

  return (
    <SimpleDrawer
      title="Rule Details"
      onClose={onClose}
      isLoading={isLoadingLogs}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ mt: -2, fontWeight: 'bold' }}>
          {selectedRule.basicInfo.ruleName || 'N/A'}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {selectedRule.basicInfo.status === 'Draft' ? (
            <StatusLabel status={'DRAFT'} />
          ) : (
            <StatusLabel status={isEnabled ? 'ENABLED' : 'DISABLED'} />
          )}
          <Tooltip title="Actions">
            <IconButton onClick={handleClick} id="rule-details-menu-button">
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            elevation={1}
            onClose={handleClose}
          >
            <DisabledTooltipMenuItem
              onClick={() => {
                onEdit(selectedRule);
              }}
              disabled={!canEditRules}
              data-disabled-reason={editDisabledReason}
            >
              {' '}
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              Edit
            </DisabledTooltipMenuItem>
            {selectedRule.basicInfo.status === 'Enabled' && (
              <DisabledTooltipMenuItem
                onClick={() => {
                  onDisable(selectedRule);
                  handleClose();
                }}
                disabled={!canChangeRuleStatus}
                data-disabled-reason={changeRuleStatusDisabledReason}
              >
                <ListItemIcon>
                  <Cancel fontSize="small" />
                </ListItemIcon>
                Disable
              </DisabledTooltipMenuItem>
            )}
            {selectedRule.basicInfo.status === 'Disabled' && (
              <DisabledTooltipMenuItem
                onClick={() => {
                  onEnable(selectedRule);
                  handleClose();
                }}
                disabled={!canChangeRuleStatus}
                data-disabled-reason={changeRuleStatusDisabledReason}
              >
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                Enable
              </DisabledTooltipMenuItem>
            )}
            <DisabledTooltipMenuItem
              onClick={() => {
                onClone(selectedRule);
              }}
              disabled={!canCloneRules}
              data-disabled-reason={cloneDisabledReason}
            >
              <ListItemIcon>
                <LibraryAdd fontSize="small" />
              </ListItemIcon>
              Clone
            </DisabledTooltipMenuItem>
            <DisabledTooltipMenuItem
              onClick={() => {
                onDelete(selectedRule);
                handleClose();
              }}
              disabled={!canDeleteRules}
              data-disabled-reason={deleteDisabledReason}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              Delete
            </DisabledTooltipMenuItem>
          </Menu>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 2 }}>
        {selectedRule.basicInfo.description}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ sm: 1 }}
        sx={{ mb: 2, alignItems: 'center' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Update fontSize="small" />
          <Typography variant="body2" color="inherit">
            Last triggered: {formatDate(lastTriggered) || 'N/A'}
          </Typography>
        </Stack>
        <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
          •
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Edit fontSize="small" />
          <Typography variant="body2" color="inherit">
            Last modified:{' '}
            {formatDate(selectedRule.basicInfo.lastModifiedDate) || 'N/A'}
          </Typography>
        </Stack>
        <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
          •
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: 'text.secondary' }}
        >
          <Check fontSize="small" />
          <Typography variant="body2" color="inherit">
            Created: {formatDate(selectedRule.basicInfo.createdDate) || 'N/A'}
          </Typography>
        </Stack>
      </Stack>

      {selectedRule.basicInfo.status !== 'Draft' && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 1, width: '100%' }}
        >
          <StatCard
            color="cyan"
            label="Total Executions"
            value={
              executionCountsData ? (
                executionCounts.total
              ) : isLoadingLogs ? (
                <Skeleton width={40} />
              ) : (
                executionCounts.total
              )
            }
            sx={{ width: '100%', minWidth: 200 }}
          />
          <StatCard
            color="teal"
            label="Successful"
            value={
              executionCountsData ? (
                executionCounts.successful
              ) : isLoadingLogs ? (
                <Skeleton width={40} />
              ) : (
                executionCounts.successful
              )
            }
            sx={{ width: '100%', minWidth: 200 }}
          />
          <StatCard
            color="rose"
            label="Failed"
            value={
              executionCountsData ? (
                executionCounts.failed
              ) : isLoadingLogs ? (
                <Skeleton width={40} />
              ) : (
                executionCounts.failed
              )
            }
            sx={{ width: '100%', minWidth: 200 }}
          />
        </Stack>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="rule details tabs"
        >
          <Tab label="Overview" />
          <Tab label="Logs" />
        </Tabs>
      </Box>
      <Box sx={{ pt: 1, pb: 4 }}>
        {tabValue === 0 && (
          <RulesDetailsSummaryOld
            selectedRule={selectedRule}
            allPolicies={allPolicies}
          />
        )}
        {tabValue === 1 && (
          <RulesEngineRunLogsGrid
            ruleId={selectedRule.id}
            policies={allPolicies || []}
            rules={rules}
          />
        )}
      </Box>
    </SimpleDrawer>
  );
}
